import { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, Box, Typography, Button } from '@mui/material';
import PauseIcon from '@mui/icons-material/PauseOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// ── Clinical Curator tokens (scoped to this screen) ─────────────────────
// Aligned with the handoff's DESIGN.md — slightly warmer than the global
// `designTheme.js` palette because the design brief specifies these hexes
// for the analysis surface. Values kept in one place so it's easy to
// fold back into the global theme later.
const T = {
  surface:           '#f8f9fb',
  surfaceLow:        '#f1f3f6',
  surfaceLowest:     '#ffffff',
  surfaceContainer:  '#eaeef2',
  surfaceContainerH: '#e2e9ee',
  onSurface:         '#2b3438',
  onSurfaceVariant:  '#586065',
  outlineVariant:    '#aab3b9',
  primary:           '#005ac4',
  primaryDim:        '#004eac',
  primaryContainer:  '#d8e2ff',
  tertiary:          '#bb1b21',
  success:           '#2f6b5a',
  warn:              '#c88b1c',
  warnDeep:          '#8b5e0e',
  ambientSoft:       '0 2px 12px rgba(43, 52, 56, 0.04)',
};

const FONT_DISPLAY = "'Manrope', sans-serif";
const FONT_UI      = "'Inter', system-ui, sans-serif";
const FONT_MONO    = "'JetBrains Mono', monospace";

// ── Keyframes (injected once, global) ────────────────────────────────────
const KEYFRAMES = `
@keyframes aap-spin { to { transform: rotate(360deg); } }
@keyframes aap-pulse {
  0%,100% { box-shadow: 0 0 0 4px rgba(187,27,33,0.08); }
  50%     { box-shadow: 0 0 0 7px rgba(187,27,33,0.18); }
}
@keyframes aap-pulse-pip { 0%,100% { opacity: .45; } 50% { opacity: 1; } }
@keyframes aap-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
@keyframes aap-slide-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
@keyframes aap-orbitA {
  from { transform: rotate(0deg)   translate(200px) rotate(0deg); }
  to   { transform: rotate(360deg) translate(200px) rotate(-360deg); }
}
@keyframes aap-orbitB {
  from { transform: rotate(120deg) translate(158px) rotate(-120deg); }
  to   { transform: rotate(480deg) translate(158px) rotate(-480deg); }
}
@keyframes aap-orbitC {
  from { transform: rotate(240deg) translate(115px) rotate(-240deg); }
  to   { transform: rotate(600deg) translate(115px) rotate(-600deg); }
}
`;

// ── Helpers ──────────────────────────────────────────────────────────────
const fmtRuntime = (sec) => {
  const m = String(Math.floor(sec / 60)).padStart(2, '0');
  const s = String(sec % 60).padStart(2, '0');
  return `00:${m}:${s}`;
};

const fmtCaseOpened = (d) => {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  const month = dt.toLocaleString('en-US', { month: 'short' });
  const day = dt.getDate();
  const year = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${month} ${day}, ${year} · ${hh}:${mm}`;
};

// Each case_type has its own roster. Order matters — it drives the on-screen
// ordering and the per-agent signal-count split.
const AGENT_ROSTERS = {
  application_fraud: [
    { key: 'fraud',           tone: 'primary', name: 'Fraud Agent',            topic: 'fraud signals'    },
    { key: 'network',         tone: 'success', name: 'Network Agent',          topic: 'network signals'  },
    { key: 'risk_multiplier', tone: 'warn',    name: 'Risk Multiplier Agent',  topic: 'risk multipliers' },
  ],
  sanctions: [
    { key: 'network',         tone: 'success', name: 'Network Agent',           topic: 'network signals'    },
    { key: 'sanctions',       tone: 'primary', name: 'Sanctions Agent',         topic: 'sanctions signals'  },
    { key: 'risk_multiplier', tone: 'warn',    name: 'Risk Multiplier Agent',   topic: 'risk multipliers'   },
  ],
};

const toneColor = (tone) =>
  tone === 'success' ? T.success : tone === 'warn' ? T.warnDeep : T.primary;
const toneDot = (tone) =>
  tone === 'success' ? T.success : tone === 'warn' ? T.warn : T.primary;

const stateLabel = (s) =>
  s === 'loading' ? 'Working' : s === 'done' ? 'Done' : s === 'error' ? 'Error' : 'Queued';

// ── Sub-components ───────────────────────────────────────────────────────

function AgentRow({ agent, agentState, signalCount }) {
  const color = toneColor(agent.tone);
  const dot = toneDot(agent.tone);
  const dotColor = agentState === 'error' ? T.tertiary : dot;
  const label = stateLabel(agentState);
  const subtitle =
    agentState === 'loading' ? `${signalCount} ${agent.topic} · analyzing`
    : agentState === 'done'  ? `${signalCount} ${agent.topic} · complete`
    : agentState === 'error' ? `${signalCount} ${agent.topic} · failed`
    :                          `${signalCount} ${agent.topic} · queued`;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: '14px', alignItems: 'center' }}>
      {/* Orb */}
      <Box sx={{
        position: 'relative',
        width: 40, height: 40, borderRadius: '50%',
        background: 'radial-gradient(circle at 50% 40%, #fff 0%, #eef2f8 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: 'inset 0 0 0 1px rgba(0,90,196,0.10)',
        '&::before': {
          content: '""',
          position: 'absolute', inset: '-2px',
          borderRadius: '50%',
          border: '1px dashed rgba(0,90,196,0.22)',
          animation: 'aap-spin 12s linear infinite',
        },
      }}>
        <Box sx={{
          width: 9, height: 9, borderRadius: '50%',
          bgcolor: dotColor,
          boxShadow: `0 0 0 4px ${dotColor}26`,
          animation: 'aap-pulse-pip 1.6s ease-in-out infinite',
        }} />
      </Box>

      {/* Meta */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '14px',
          letterSpacing: '0.01em', color: T.onSurface, lineHeight: 1.2,
        }}>
          {agent.name}
        </Typography>
        <Typography sx={{
          fontFamily: FONT_UI, fontSize: '11.5px',
          color: T.onSurfaceVariant, mt: '2px',
        }}>
          {subtitle}
        </Typography>
      </Box>

      {/* State tag */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '6px',
        color,
        fontFamily: FONT_UI, fontSize: '10.5px', fontWeight: 600,
        letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>
        <Box sx={{
          width: 5, height: 5, borderRadius: '999px',
          bgcolor: 'currentColor',
          animation: agentState === 'loading' ? 'aap-pulse-pip 1.4s ease-in-out infinite' : 'none',
        }} />
        {label}
      </Box>
    </Box>
  );
}

function PhaseRail({ phaseStates, phaseLabels }) {
  return (
    <Box sx={{ mt: 'auto', pt: '24px' }}>
      <Box sx={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '6px', mb: '10px',
      }}>
        {phaseStates.map((s, i) => {
          const base = {
            height: 4, borderRadius: 2,
            bgcolor: T.surfaceContainer,
          };
          if (s === 'done') return <Box key={i} sx={{ ...base, bgcolor: T.primary }} />;
          if (s === 'active') return (
            <Box key={i} sx={{
              ...base,
              background: `linear-gradient(90deg, ${T.primary} 60%, ${T.surfaceContainer} 60%)`,
              position: 'relative', overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute', inset: 0,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)',
                animation: 'aap-shimmer 1.8s ease-in-out infinite',
              },
            }} />
          );
          return <Box key={i} sx={base} />;
        })}
      </Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between',
        fontFamily: FONT_UI, fontSize: '11px', color: T.onSurfaceVariant,
      }}>
        {phaseLabels.map((l, i) => (
          <Box
            key={l}
            component="span"
            sx={phaseStates[i] === 'active'
              ? { color: T.primary, fontWeight: 700 }
              : undefined
            }
          >
            {l}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function GlobeStage({ coreStatus, redFlagCount, activeAgentCount }) {
  return (
    <Box sx={{
      position: 'relative',
      bgcolor: T.surfaceLowest,
      borderRadius: '1rem',
      overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: T.ambientSoft,
    }}>
      {/* Grid background */}
      <Box sx={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(${T.surfaceContainer} 1px, transparent 1px),
          linear-gradient(90deg, ${T.surfaceContainer} 1px, transparent 1px)
        `,
        backgroundSize: '56px 56px',
        opacity: 0.45,
        maskImage: 'radial-gradient(circle at 50% 50%, black 45%, transparent 82%)',
        WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 45%, transparent 82%)',
      }} />

      {/* Corner labels */}
      {[
        { pos: { top: 20, left: 24 },   text: '◜ Analysis Field' },
        { pos: { top: 20, right: 24 },  text: 'Protocol · v3.2' },
        { pos: { bottom: 20, left: 24 },  text: 'Node · ATL-04' },
        { pos: { bottom: 20, right: 24 }, text: 'Ref · MG-24081' },
      ].map(({ pos, text }, i) => (
        <Typography key={i} sx={{
          position: 'absolute', ...pos,
          fontFamily: FONT_MONO, fontSize: '10px',
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: T.onSurfaceVariant,
        }}>
          {text}
        </Typography>
      ))}

      {/* Globe */}
      <Box sx={{
        position: 'relative', width: 460, height: 460,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {/* Rings */}
        <Box sx={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          border: '1px dashed rgba(0,90,196,0.18)',
          animation: 'aap-spin 50s linear infinite',
        }} />
        <Box sx={{
          position: 'absolute', inset: '44px', borderRadius: '50%',
          border: '1px solid rgba(0,90,196,0.08)',
        }} />
        <Box sx={{
          position: 'absolute', inset: '88px', borderRadius: '50%',
          border: '1px dashed rgba(0,90,196,0.14)',
          animation: 'aap-spin 32s linear infinite reverse',
        }} />
        <Box sx={{
          position: 'absolute', inset: '132px', borderRadius: '50%',
          border: '1px solid rgba(0,90,196,0.06)',
        }} />

        {/* Scan sweep */}
        <Box sx={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'conic-gradient(from 0deg, rgba(0,90,196,0) 0deg, rgba(0,90,196,0.12) 50deg, rgba(0,90,196,0) 100deg)',
          animation: 'aap-spin 5s linear infinite',
          mixBlendMode: 'multiply',
        }} />

        {/* Orbit dots — transform-origin at top-left, so we add a centering wrapper */}
        {[
          { radius: 200, color: T.primary, anim: 'aap-orbitA 10s linear infinite' },
          { radius: 158, color: T.success, anim: 'aap-orbitB 13s linear infinite' },
          { radius: 115, color: T.warn,    anim: 'aap-orbitC 7.5s linear infinite' },
        ].map((d, i) => (
          <Box key={i} sx={{
            position: 'absolute',
            top: '50%', left: '50%',
            width: 9, height: 9, borderRadius: '50%',
            marginTop: '-4.5px', marginLeft: '-4.5px',
            transformOrigin: '0 0',
            bgcolor: d.color,
            boxShadow: `0 0 0 5px ${d.color}22`,
            animation: d.anim,
          }} />
        ))}

        {/* Core */}
        <Box sx={{
          position: 'relative',
          width: 210, height: 210, borderRadius: '50%',
          background: 'radial-gradient(circle at 50% 40%, #ffffff 0%, #f2f6fc 55%, #e2e9ee 100%)',
          boxShadow: `
            0 2px 0 rgba(0,90,196,0.04) inset,
            0 18px 40px rgba(0,90,196,0.10),
            0 0 0 8px rgba(0,90,196,0.04)
          `,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: '10px',
        }}>
          {/* Top pill */}
          <Box sx={{
            position: 'absolute', top: -14, left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: T.primary, color: '#fff',
            fontFamily: FONT_UI,
            fontSize: '10px', fontWeight: 600,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            px: '12px', py: '6px',
            borderRadius: '999px',
            boxShadow: '0 6px 18px rgba(0,90,196,0.30)',
            display: 'flex', alignItems: 'center', gap: '6px',
            whiteSpace: 'nowrap',
          }}>
            <Box sx={{
              width: 5, height: 5, borderRadius: '50%',
              bgcolor: '#fff',
              animation: 'aap-pulse-pip 1.4s ease-in-out infinite',
            }} />
            Processing
          </Box>

          <Typography sx={{
            fontFamily: FONT_UI, fontSize: '10px',
            letterSpacing: '0.2em', textTransform: 'uppercase',
            fontWeight: 600, color: T.onSurfaceVariant,
          }}>
            Orchestrator
          </Typography>
          <Typography sx={{
            fontFamily: FONT_DISPLAY, fontWeight: 700,
            fontSize: '22px', letterSpacing: '-0.01em',
            color: T.onSurface,
          }}>
            Master Agent
          </Typography>
          <Typography sx={{
            fontFamily: FONT_MONO, fontSize: '11px',
            color: T.primary, fontWeight: 600, letterSpacing: '0.06em',
          }}>
            {coreStatus}
          </Typography>
        </Box>
      </Box>

      {/* Caption under globe */}
      <Typography sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, 170px)',
        textAlign: 'center',
        fontFamily: FONT_MONO, fontSize: '11px',
        letterSpacing: '0.14em', textTransform: 'uppercase',
        color: T.onSurfaceVariant,
        whiteSpace: 'nowrap',
      }}>
        Reviewing{' '}
        <Box component="strong" sx={{ color: T.onSurface, fontWeight: 600 }}>
          {activeAgentCount} specialist agents
        </Box>
        {' · '}
        <Box component="strong" sx={{ color: T.onSurface, fontWeight: 600 }}>
          {redFlagCount} red-flag signals
        </Box>
      </Typography>
    </Box>
  );
}

function SummaryCard({ phaseDisplay, redFlagCount, runtimeSec, verdictReady, agentCount = 3 }) {
  const rows = [
    {
      k: 'Phase',
      v: phaseDisplay,
      vSx: { color: verdictReady ? T.success : T.primary, fontFamily: FONT_DISPLAY, fontSize: '15px', fontWeight: 700 },
    },
    { k: 'Red flags in case', v: String(redFlagCount), mono: true },
    { k: 'Agents deployed',   v: String(agentCount), mono: true },
    { k: 'Runtime',           v: fmtRuntime(runtimeSec), mono: true },
  ];

  return (
    <Box sx={{
      bgcolor: T.surfaceLowest,
      borderRadius: '1rem',
      px: '24px', py: '22px',
      boxShadow: T.ambientSoft,
    }}>
      {rows.map((r, i) => (
        <Box key={r.k} sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          py: '8px',
          ...(i > 0 && { borderTop: '1px solid rgba(170,179,185,0.18)' }),
        }}>
          <Typography sx={{ fontFamily: FONT_UI, fontSize: '12px', color: T.onSurfaceVariant }}>
            {r.k}
          </Typography>
          <Typography sx={r.vSx ?? (r.mono
            ? { fontFamily: FONT_MONO, fontWeight: 700, fontSize: '13px', letterSpacing: '0.02em', color: T.onSurface }
            : { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '15px', color: T.onSurface }
          )}>
            {r.v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function ActivityCard({ events }) {
  const listRef = useRef(null);

  return (
    <Box sx={{
      bgcolor: T.surfaceLowest,
      borderRadius: '1rem',
      px: '24px', py: '22px',
      boxShadow: T.ambientSoft,
      display: 'flex', flexDirection: 'column',
      minHeight: 0, flex: 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography sx={{
          fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '13px',
          letterSpacing: '0.08em', textTransform: 'uppercase', color: T.onSurface,
        }}>
          Activity
        </Typography>
        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '11px', color: T.onSurfaceVariant }}>
          live
        </Typography>
      </Box>

      <Box ref={listRef} sx={{
        mt: '14px', flex: 1, overflow: 'hidden', position: 'relative',
        maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
      }}>
        {events.map((ev, i) => {
          const dotColor =
            ev.kind === 'success' ? T.success
            : ev.kind === 'warn' ? T.warn
            : ev.kind === 'error' ? T.tertiary
            : T.primary;
          return (
            <Box key={ev.id} sx={{
              display: 'grid', gridTemplateColumns: '14px 1fr',
              gap: '12px', py: '12px',
              fontFamily: FONT_UI, fontSize: '13px', color: T.onSurface,
              alignItems: 'start',
              borderBottom: i === events.length - 1 ? 'none' : '1px solid rgba(170,179,185,0.12)',
              animation: 'aap-slide-in 0.5s ease',
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: '6px', bgcolor: dotColor }} />
              <Box>
                {ev.message}
                <Typography sx={{
                  fontFamily: FONT_MONO, fontSize: '10.5px',
                  color: T.onSurfaceVariant, mt: '3px', letterSpacing: '0.04em',
                }}>
                  {ev.when}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Main component ──────────────────────────────────────────────────────

export default function AgentAnalysisModal({
  open,
  caseNumber,
  caseType = 'sanctions',
  agentProgress,
  agentSummaries,
  onViewVerdict,
  caseData,
  redFlagCount = 0,
}) {
  const roster = AGENT_ROSTERS[caseType] || AGENT_ROSTERS.sanctions;
  const agentKeys = roster.map((a) => a.key);
  // Runtime
  const [runtimeSec, setRuntimeSec] = useState(0);
  useEffect(() => {
    if (!open) return;
    setRuntimeSec(0);
    const id = setInterval(() => setRuntimeSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  // Core % drift
  const [corePct, setCorePct] = useState(34);
  useEffect(() => {
    if (!open) return;
    setCorePct(34);
    const id = setInterval(() => {
      setCorePct((p) => Math.min(78, p + Math.random() * 0.8));
    }, 1800);
    return () => clearInterval(id);
  }, [open]);

  // Phase rail — derived from agent progress
  const phaseLabels = ['Collect', 'Analyze', 'Synthesize', 'Assess'];
  const rfAllResolved = agentKeys.every(
    (k) => agentProgress[k] === 'done' || agentProgress[k] === 'error'
  );
  const anyRfLoading = agentKeys.some((k) => agentProgress[k] === 'loading');
  const verdictReady = agentProgress.master === 'done';
  const masterLoading = agentProgress.master === 'loading';

  const phaseStates = (() => {
    // Collect: always done once we've opened the modal
    let collect = 'done';
    // Analyze: active while rf agents are running; done once all resolved
    let analyze = rfAllResolved ? 'done' : (anyRfLoading ? 'active' : 'todo');
    // Synthesize: active during master loading; done when master done
    let synthesize = verdictReady ? 'done' : (masterLoading ? 'active' : 'todo');
    // Assess: active once master is done (about to transition to report)
    let assess = verdictReady ? 'active' : 'todo';
    return [collect, analyze, synthesize, assess];
  })();

  const currentPhaseLabel = verdictReady ? 'Assessment Ready'
    : masterLoading ? 'Synthesizing'
    : anyRfLoading ? 'Running Analysis'
    : 'Initializing';

  const summaryPhaseDisplay = verdictReady ? 'Assessment ready'
    : masterLoading ? 'Synthesizing findings'
    : 'Analyzing signals';

  // Core status line
  const coreStatus = verdictReady ? 'synthesis complete · 100%'
    : masterLoading ? `synthesizing · ${Math.max(corePct, 80).toFixed(0)}%`
    : `awaiting agents · ${corePct.toFixed(0)}%`;

  // Per-agent signal counts — proportional split of total red flags across 3 agents.
  // Use the roster order so the split lines up with the visible tiles.
  const perAgentCounts = useMemo(() => {
    const total = Math.max(0, redFlagCount);
    const result = {};
    agentKeys.forEach((k) => { result[k] = 0; });
    if (total === 0 || agentKeys.length === 0) return result;
    // Default split: 50/30/20 (rounded but summing back to total).
    const first  = Math.round(total * 0.5);
    const second = Math.round(total * 0.3);
    const third  = Math.max(0, total - first - second);
    const splits = [first, second, third];
    agentKeys.forEach((k, i) => { result[k] = splits[i] ?? 0; });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redFlagCount, caseType]);

  // Activity stream — seeded, rotates on timer + reacts to state changes
  const prevProgressRef = useRef({ master: 'idle' });
  const [events, setEvents] = useState([]);
  const eventIdRef = useRef(0);

  const pushEvent = (kind, message) => {
    eventIdRef.current += 1;
    setEvents((prev) => {
      const next = [{ id: eventIdRef.current, kind, message, when: 'just now' }, ...prev];
      // Re-age older rows
      next.forEach((e, i) => {
        if (i === 0) e.when = 'just now';
        else if (i === 1) e.when = '10s ago';
        else if (i === 2) e.when = '30s ago';
        else if (i === 3) e.when = '1m ago';
        else e.when = '1m+ ago';
      });
      return next.slice(0, 6);
    });
  };

  // Seed events when the modal opens — use the first two names from the
  // active roster so the canned copy matches whichever agents are running.
  useEffect(() => {
    if (!open) return;
    eventIdRef.current = 0;
    const [a0, a1, a2] = roster;
    setEvents([
      { id: -1, kind: 'success', message: `${a1?.name || 'Network Agent'} mapped 2 counter-party links.`, when: 'just now' },
      { id: -2, kind: 'info',    message: (<>{a0?.name || 'Fraud Agent'} processed signal <Box component="span" sx={{ fontFamily: FONT_MONO }}>FR-07</Box>.</>), when: '12s ago' },
      { id: -3, kind: 'error',   message: (<>{a0?.name || 'Fraud Agent'} flagged anomaly on signal <Box component="span" sx={{ fontFamily: FONT_MONO }}>FR-03</Box>.</>), when: '38s ago' },
      { id: -4, kind: 'info',    message: 'Master Agent began correlating agent outputs.', when: '1m ago' },
      { id: -5, kind: 'warn',    message: `${a2?.name || 'Third Agent'} queued pending upstream completion.`, when: '1m 22s ago' },
    ]);
    prevProgressRef.current = { master: 'idle' };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseType]);

  // React to progress transitions — push meaningful events
  useEffect(() => {
    if (!open) return;
    const prev = prevProgressRef.current;
    const agentNames = Object.fromEntries(roster.map((a) => [a.key, a.name]));
    agentKeys.forEach((k) => {
      if (agentProgress[k] !== prev[k]) {
        if (agentProgress[k] === 'loading') pushEvent('info',    `${agentNames[k]} began processing signals.`);
        if (agentProgress[k] === 'done')    pushEvent('success', `${agentNames[k]} completed its analysis.`);
        if (agentProgress[k] === 'error')   pushEvent('error',   `${agentNames[k]} reported a processing error.`);
      }
    });
    if (agentProgress.master !== prev.master) {
      if (agentProgress.master === 'loading') pushEvent('info',    'Master Agent began synthesizing findings.');
      if (agentProgress.master === 'done')    pushEvent('success', 'Master Agent produced the risk assessment.');
      if (agentProgress.master === 'error')   pushEvent('error',   'Master Agent failed to synthesize findings.');
    }
    prevProgressRef.current = { ...agentProgress };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentProgress, open]);

  // Background drift — keeps the feed alive while agents are running.
  // Templates reference roster names so copy changes with the case_type.
  useEffect(() => {
    if (!open) return;
    const [a0, a1, a2] = roster;
    const templates = [
      { kind: 'info',    msg: `${a0?.name || 'Agent 1'} processed a new red-flag signal.` },
      { kind: 'success', msg: `${a1?.name || 'Agent 2'} mapped a counter-party link.` },
      { kind: 'info',    msg: 'Master Agent updated the evidence synthesis.' },
      { kind: 'warn',    msg: `${a2?.name || 'Agent 3'} deferred a low-confidence input.` },
      { kind: 'error',   msg: `${a0?.name || 'Agent 1'} flagged a high-severity signal.` },
      { kind: 'info',    msg: `${a1?.name || 'Agent 2'} strengthened a counter-party cluster.` },
    ];
    const id = setInterval(() => {
      if (verdictReady) return;
      const t = templates[Math.floor(Math.random() * templates.length)];
      pushEvent(t.kind, t.msg);
    }, 3600);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, verdictReady, caseType]);

  // Derived header data
  const analystName = caseData?.assigned_to || caseData?.analyst || 'Investigator';
  const caseOpenedDisplay = fmtCaseOpened(
    caseData?.settlement_date || caseData?.opened_at || caseData?.created_at
  );
  const activeAgentCount = agentKeys.filter(
    (k) => agentProgress[k] === 'loading' || agentProgress[k] === 'done'
  ).length || agentKeys.length;

  return (
    <Dialog
      open={open}
      fullScreen
      slotProps={{
        paper: {
          sx: {
            bgcolor: T.surface,
            color: T.onSurface,
            overflow: 'hidden',
            overflowX: 'auto',
          },
        },
      }}
    >
      {/* Global keyframes */}
      <style>{KEYFRAMES}</style>

      <Box sx={{
        minWidth: 1360, minHeight: '100vh',
        display: 'grid',
        gridTemplateRows: '76px 1fr 72px',
        fontFamily: FONT_UI,
      }}>

        {/* ═══ HEADER ═══════════════════════════════════════════════ */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          px: '40px',
        }}>
          <Box>
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', mb: '4px' }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '999px',
                bgcolor: T.tertiary,
                boxShadow: '0 0 0 4px rgba(187, 27, 33, 0.12)',
                animation: 'aap-pulse 1.8s ease-in-out infinite',
              }} />
              <Typography sx={{
                fontFamily: FONT_UI, fontSize: '0.6875rem', fontWeight: 600,
                letterSpacing: '0.09em', textTransform: 'uppercase',
                color: T.tertiary,
              }}>
                Active Investigation
              </Typography>
              <Typography sx={{
                fontFamily: FONT_UI, fontSize: '0.6875rem', fontWeight: 600,
                letterSpacing: '0.09em', textTransform: 'uppercase',
                color: T.onSurfaceVariant,
              }}>
                Case ·{' '}
                <Box component="span" sx={{ fontFamily: FONT_MONO, color: T.onSurface }}>
                  {caseNumber}
                </Box>
              </Typography>
            </Box>
            <Typography sx={{
              fontFamily: FONT_DISPLAY, fontWeight: 800,
              fontSize: '28px', letterSpacing: '-0.02em',
              color: T.onSurface, m: 0, lineHeight: 1.1,
            }}>
              Agent Analysis Protocol
            </Typography>
          </Box>

          {/* Case / analyst card */}
          <Box sx={{
            bgcolor: T.surfaceLowest,
            borderRadius: '0.625rem',
            px: '20px', py: '12px',
            display: 'flex', alignItems: 'center', gap: '20px',
            boxShadow: T.ambientSoft,
          }}>
            <Box sx={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
              <Box>
                <Typography sx={{
                  fontFamily: FONT_UI, fontSize: '0.6875rem', fontWeight: 600,
                  letterSpacing: '0.09em', textTransform: 'uppercase',
                  color: T.onSurfaceVariant, mb: '4px',
                }}>
                  Case opened
                </Typography>
                <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '15px', color: T.onSurface }}>
                  {caseOpenedDisplay}
                </Typography>
              </Box>
              <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: T.surfaceContainer }} />
              <Box>
                <Typography sx={{
                  fontFamily: FONT_UI, fontSize: '0.6875rem', fontWeight: 600,
                  letterSpacing: '0.09em', textTransform: 'uppercase',
                  color: T.onSurfaceVariant, mb: '4px',
                }}>
                  Analyst
                </Typography>
                <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '15px', color: T.onSurface }}>
                  {analystName}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ═══ MAIN ═════════════════════════════════════════════════ */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '320px 1fr 340px',
          gap: '24px',
          px: '32px', pt: '8px', pb: '24px',
          minHeight: 0,
        }}>
          {/* Left: agents panel */}
          <Box sx={{
            bgcolor: T.surfaceLowest,
            borderRadius: '1rem',
            px: '26px', pt: '26px', pb: '24px',
            boxShadow: T.ambientSoft,
            display: 'flex', flexDirection: 'column',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '22px' }}>
              <Typography sx={{
                fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '13px',
                letterSpacing: '0.08em', textTransform: 'uppercase', color: T.onSurface,
              }}>
                Agents
              </Typography>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: '11px', color: T.onSurfaceVariant }}>
                {activeAgentCount} active
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {roster.map((agent) => (
                <AgentRow
                  key={agent.key}
                  agent={agent}
                  agentState={agentProgress[agent.key]}
                  signalCount={perAgentCounts[agent.key]}
                />
              ))}
            </Box>

            <PhaseRail phaseStates={phaseStates} phaseLabels={phaseLabels} />
          </Box>

          {/* Center: stage */}
          <GlobeStage
            coreStatus={coreStatus}
            redFlagCount={redFlagCount || 16}
            activeAgentCount={activeAgentCount}
          />

          {/* Right column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', minHeight: 0 }}>
            <SummaryCard
              phaseDisplay={summaryPhaseDisplay}
              redFlagCount={redFlagCount || 16}
              runtimeSec={runtimeSec}
              verdictReady={verdictReady}
              agentCount={roster.length}
            />
            <ActivityCard events={events} />
          </Box>
        </Box>

        {/* ═══ FOOTER ═══════════════════════════════════════════════ */}
        <Box sx={{
          bgcolor: T.surfaceLowest,
          display: 'grid',
          gridTemplateColumns: 'auto auto 1fr auto',
          alignItems: 'center',
          gap: '32px',
          px: '40px',
          boxShadow: `0 -1px 0 ${T.surfaceContainer}`,
        }}>
          {/* Current phase */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box sx={{
              width: 9, height: 9, borderRadius: '999px',
              bgcolor: verdictReady ? T.success : T.primary,
              boxShadow: verdictReady
                ? '0 0 0 5px rgba(47,107,90,0.14)'
                : '0 0 0 5px rgba(0,90,196,0.14)',
              animation: 'aap-pulse-pip 1.4s ease-in-out infinite',
            }} />
            <Box>
              <Typography sx={{
                fontFamily: FONT_UI, fontSize: '10.5px', fontWeight: 600,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: T.onSurfaceVariant,
              }}>
                Current Phase
              </Typography>
              <Typography sx={{
                fontFamily: FONT_DISPLAY, fontWeight: 700,
                fontSize: '15px', letterSpacing: '-0.005em',
                color: verdictReady ? T.success : T.primary,
              }}>
                {currentPhaseLabel}
              </Typography>
            </Box>
          </Box>

          {/* Deliverable */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box sx={{
              width: 9, height: 9, borderRadius: '999px',
              bgcolor: T.success,
              boxShadow: '0 0 0 5px rgba(47,107,90,0.14)',
              animation: 'aap-pulse-pip 1.4s ease-in-out infinite',
            }} />
            <Box>
              <Typography sx={{
                fontFamily: FONT_UI, fontSize: '10.5px', fontWeight: 600,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: T.onSurfaceVariant,
              }}>
                Deliverable
              </Typography>
              <Typography sx={{
                fontFamily: FONT_DISPLAY, fontWeight: 700,
                fontSize: '15px', letterSpacing: '-0.005em',
                color: T.success,
              }}>
                Risk Assessment &amp; Recommendations
              </Typography>
            </Box>
          </Box>

          {/* Runtime */}
          <Typography sx={{
            fontFamily: FONT_MONO, fontSize: '13px',
            color: T.onSurfaceVariant, letterSpacing: '0.04em',
          }}>
            Session{' '}
            <Box component="strong" sx={{ color: T.onSurface, fontWeight: 600 }}>
              {fmtRuntime(runtimeSec)}
            </Box>
            {' · ETA '}
            <Box component="strong" sx={{ color: T.onSurface, fontWeight: 600 }}>
              {verdictReady ? 'ready' : '42s'}
            </Box>
          </Typography>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: '10px', justifySelf: 'end' }}>
            <Button
              startIcon={<PauseIcon sx={{ fontSize: 14 }} />}
              sx={{
                fontFamily: FONT_UI, fontWeight: 600, fontSize: '12px',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                px: '18px', py: '11px',
                borderRadius: '0.375rem',
                bgcolor: T.surfaceContainerH, color: T.onSurface,
                '&:hover': { bgcolor: T.surfaceContainerH, filter: 'brightness(0.97)' },
              }}
            >
              Pause
            </Button>
            <Button
              onClick={verdictReady ? onViewVerdict : undefined}
              disabled={!verdictReady}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              sx={{
                fontFamily: FONT_UI, fontWeight: 600, fontSize: '12px',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                px: '18px', py: '11px',
                borderRadius: '0.375rem',
                color: '#fff',
                background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDim} 100%)`,
                boxShadow: '0 4px 14px rgba(0,90,196,0.22)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDim} 100%)`,
                  filter: 'brightness(1.05)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255,255,255,0.75)',
                  background: `linear-gradient(135deg, ${T.primary}88 0%, ${T.primaryDim}88 100%)`,
                  boxShadow: 'none',
                },
              }}
            >
              View Assessment
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

